class Drawer extends HTMLElement {
  constructor() {
    super();
    this.animationDuration =
      this.getAttribute("data-animation-duration") || 400;

    if (typeof this.animationDuration == "string") {
      this.animationDuration = parseInt(this.animationDuration);
    }

    this.isSubDrawer = this.getAttribute("data-subddrawer");
    this.isInMovement = false;

    this.closeOnEscNamed = this.closeOnEsc.bind(this);
    this.closeOnBlurNamed = this.closeOnBlur.bind(this);

    this.blurNodeTypes = ["BODY", "CART-DRAWER", "DRA-WER"];
  }

  setDrawerTargets() {
    this.querySelectorAll("drawer-control").forEach((elem) => {
      if (!elem.hasAttribute("for")) {
        elem.setAttribute("for", this.getAttribute("id"));
      }
    });
  }

  // Stacked drawers (cart over gallery): one Esc or backdrop click closes only the last one opened.
  isTopmost(event) {
    if (event.drawerHandled) return false;
    return Array.from(Drawer.openDrawers).pop() === this;
  }

  closeOnBlur(event) {
    if (this.blurNodeTypes.includes(event.target.nodeName) && this.isTopmost(event)) {
      event.drawerHandled = true;
      this.closeDrawer();
    }
  }

  closeOnEsc(event) {
    if (event.keyCode == 27 && this.isTopmost(event)) {
      event.drawerHandled = true;
      this.closeDrawer();
    }
  }

  bindWindowEvents() {
    window.addEventListener("keydown", this.closeOnEscNamed);
    window.addEventListener("click", this.closeOnBlurNamed);
  }

  unbindWindowEvents() {
    window.removeEventListener("keydown", this.closeOnEscNamed);
    window.removeEventListener("click", this.closeOnBlurNamed);
  }

  isMoving() {
    this.isInMovement = true;
    this.setAttribute("is-moving", "");

    setTimeout(() => {
      this.removeAttribute("is-moving");
      this.isInMovement = false;
    }, this.animationDuration);
  }

  // Opt-in (data-manage-focus): closed, the drawer is inert, so neither Tab nor a screen reader reaches it off screen;
  // opened, focus moves in ([data-autofocus] or the first control); closed again, it returns to where it was.
  // data-focus-scope (a selector of an ancestor): Tab stays inside that instead, for a drawer with panels beside it
  // (the menu and its sub-levels in nav-drawer).
  manageFocus(open) {
    if (!this.hasAttribute("data-manage-focus")) return;
    if (!this.trapFocusNamed) this.trapFocusNamed = this.trapFocus.bind(this);
    if (!this.focusScope) this.focusScope = (this.dataset.focusScope && this.closest(this.dataset.focusScope)) || this;
    if (!this.tabInNamed) this.tabInNamed = this.tabIn.bind(this);
    if (open) {
      this.focusScope.addEventListener("keydown", this.trapFocusNamed);
      window.addEventListener("keydown", this.tabInNamed);
      this.removeAttribute("inert");
      // Opened again while open (an add from inside the cart re-renders it and opens it once more): the first opener
      // stays the one focus goes back to.
      if (!this.focusOpen) this.opener = document.activeElement;
      this.focusOpen = true;
      // data-manage-focus="keyboard" (the cart): focus moves in only when a key opened it; opened by a click or tap
      // (an add to cart), focus stays where it was, as before.
      if (this.dataset.manageFocus === "keyboard" && !Drawer.keyboardInput) return;
      requestAnimationFrame(() => {
        const target = this.querySelector("[data-autofocus]") || this.querySelector("drawer-control, button, a[href]");
        if (target) target.focus({ preventScroll: true });
      });
    } else {
      this.focusScope.removeEventListener("keydown", this.trapFocusNamed);
      window.removeEventListener("keydown", this.tabInNamed);
      this.focusOpen = false;
      this.setAttribute("inert", "");
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }
  }

  // Focus outside the open drawer (it opened from a click, or a re-render dropped the focused control): Tab goes into
  // it, not on through the page it covers. Only the drawer in front.
  tabIn(event) {
    if (event.key !== "Tab" || Array.from(Drawer.openDrawers).pop() !== this) return;
    if (this.focusScope.contains(document.activeElement)) return;
    // What Tab can reach in it now (not the menu under an open sub-level, which is inert): its [data-autofocus] first.
    const items = this.focusables();
    const target = items.find((el) => el.hasAttribute("data-autofocus")) || items[0];
    if (!target) return;
    event.preventDefault();
    target.focus({ preventScroll: true });
  }

  // The controls Tab can reach in its scope: shown and not inert.
  focusables() {
    var scope = this.focusScope || this;
    return Array.from(scope.querySelectorAll('drawer-control, button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null && !el.closest("[inert]"));
  }

  // Tab and Shift+Tab stay inside the open drawer: the page behind it is covered, so focus there is lost to the eye.
  trapFocus(event) {
    if (event.key !== "Tab") return;
    var items = this.focusables();
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Its open controls that say whether it is open (aria-expanded, e.g. the header's menu button) follow it.
  syncExpanded(open) {
    if (!this.id) return;
    document.querySelectorAll('drawer-control[action="open"][aria-expanded]').forEach((control) => {
      if (control.getAttribute("for") === this.id) control.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  closeDrawer() {
    this.isMoving();
    this.removeAttribute("open");
    this.syncExpanded(false);
    this.manageFocus(false);

    if (!this.isSubDrawer) {
      this.unbindWindowEvents();
      // Shared by stacked drawers (gallery under cart): release it only when the last one closes.
      Drawer.openDrawers.delete(this);
      if (!Drawer.openDrawers.size) {
        document.body.classList.remove("fade-body");
      }
    }
  }

  openDrawer() {
    this.isMoving();
    this.setAttribute("open", "");
    this.syncExpanded(true);
    this.manageFocus(true);

    if (!this.isSubDrawer) {
      this.bindWindowEvents();
      Drawer.openDrawers.delete(this);
      Drawer.openDrawers.add(this);
      document.body.classList.add("fade-body");
    }
  }

  addAnimationClass() {
    if (!this.classList.contains("allow-modal-transition")) {
      this.classList.add("allow-modal-transition");
    }
  }

  connectedCallback() {
    this.addAnimationClass();
    this.setDrawerTargets();
  }
}

class DrawerControl extends HTMLElement {
  constructor() {
    super();

    this.action = this.getAttribute("action") || "close";
    this.drawerTarget = document.querySelector(
      ".js-drawer#" + this.getAttribute("for")
    );
  }

  onInteraction() {
    // Looked up again: a control can come before its drawer in the page (the PDP gift box before the gift info).
    if (!this.drawerTarget) this.drawerTarget = document.querySelector(".js-drawer#" + this.getAttribute("for"));
    if (!this.drawerTarget) return;
    if (this.action == "close") {
      this.drawerTarget.closeDrawer();
    }
    if (this.action == "open") {
      this.drawerTarget.openDrawer();
    }
  }

  listenForInteraction() {
    this.addEventListener("click", this.onInteraction);
    this.addEventListener("keydown", (event) => {
      // Only the control itself: a key pressed in a field nested inside it is not a press of the control.
      if (event.target !== this) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.onInteraction();
      }
    });
  }

  connectedCallback() {
    this.listenForInteraction();
  }
}

Drawer.openDrawers = new Set();

// The last input was a key (true) or a pointer (false), for drawers that move focus in for the keyboard only.
Drawer.keyboardInput = false;
window.addEventListener("keydown", (event) => {
  if (!event.metaKey && !event.ctrlKey && !event.altKey) Drawer.keyboardInput = true;
}, true);
window.addEventListener("pointerdown", () => {
  Drawer.keyboardInput = false;
}, true);

customElements.define("dra-wer", Drawer);
customElements.define("drawer-control", DrawerControl);

class SearchModal extends Drawer {
  constructor() {
    super();
  }

  openDrawer() {
    super.openDrawer();
    this.searchInput.focus();
  }

  connectedCallback() {
    this.searchInput = this.querySelector('search-form input[name="q"]');
  }
}

customElements.define("search-modal", SearchModal);

class BISModal extends Drawer {
  constructor() {
    super();
  }

  bisSignup() {
    this.cacheFormData();
    var variantId = "$shopify:::$default:::" + this.formData.get("variant");
    var userEmail = this.formData.get("email");
    var companyId = this.formData.get("company_id");
    fetch(
      "https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=" +
        companyId,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          revision: "2024-02-15",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: { email: userEmail },
                },
              },
              channels: ["EMAIL"],
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: variantId,
                },
              },
            },
          },
        }),
      }
    )
      .then((response) => {
        this.form.classList.remove("bisform--error");
        this.form.classList.remove("bisform--submitted");

        if (response.ok) {
          this.form.classList.add("bisform--submitted");
        } else {
          this.form.classList.add("bisform--error");
        }
      })
      .catch((err) => {});
  }

  newslettermailSignup() {
    this.cacheFormData();
    var userEmail = this.formData.get("email");
    var listId = this.formData.get("list_id");

    fetch(
      "https://a.klaviyo.com/client/subscriptions/?company_id=" +
        this.formData.get("company_id"),
      {
        method: "POST",
        headers: {
          revision: "2024-02-15",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "bis-signup",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: userEmail,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: { type: "list", id: listId },
              },
            },
          },
        }),
      }
    )
      .then((response) => {
      })
      .catch((err) => {});
  }

  closeDrawer() {
    this.form.classList.remove("bisform--submitted");
    this.form.classList.remove("bisform--error");
    super.closeDrawer();
  }

  openDrawer() {
    this.form.classList.remove("bisform--submitted");
    this.form.classList.remove("bisform--error");
    super.openDrawer();
    this.emailInputField.focus();
  }

  cacheFormData() {
    this.form = this.querySelector("form");
    this.formData = new FormData(this.form);
  }

  setFormId() {
    this.form.querySelector('input[name="variant"]').value = this.variant_id;
    this.cacheFormData();
  }

  onFormSubmit(e) {
    e.preventDefault();

    if (this.hasNewsletterSignup) {
      this.newslettermailSignup();
    }

    this.bisSignup();
  }

  connectedCallback() {
    this.dispatchEvent(new CustomEvent("modal:loaded"));
    this.form = this.querySelector("form");
    this.emailInputField = this.form.querySelector('input[name="email"]');
    this.hasNewsletterSignup = false;

    if (this.querySelector("#bis-newsletter-signup")) {
      this.hasNewsletterSignup = true;
    }

    document.body.addEventListener("bis:trigger:click", (e) => {
      this.variant_id = e.detail.variant_id;
      this.form_bis = e.detail.form_bis;

      this.form.innerHTML = this.form_bis.innerHTML;
      this.form = this.querySelector('form');

      this.openDrawer();
      this.setFormId();
    });

    this.form.addEventListener("submit", (e) => {
      this.onFormSubmit(e);
    });
  }
}

customElements.define("bis-modal", BISModal);

class AsyncModal extends Drawer {
  constructor() {
    super();
  }

  openDrawer() {
    super.openDrawer();

    // Children that can load themselves do so on first open.
    this.querySelectorAll("modalusp-content, [data-async-content]").forEach(
      (el) => {
        if (typeof el.fetchContent === "function") {
          el.fetchContent();
        }
      }
    );
  }
}

customElements.define("async-modal", AsyncModal);

// The USP popups (util-modals: guarantee, delivery, dermatologically tested …) load their body on first open, on any page.
class ModalUSPContent extends HTMLElement {
  constructor() {
    super();

    this.loaded = false;
  }

  fetchContent() {
    if (this.loaded) {
      return;
    }

    this.sectionId = this.getAttribute("data-section");
    this.pageHandle = this.getAttribute("data-page");
    fetch(
      Shopify.routes.root +
        "pages/" +
        this.pageHandle +
        "?section_id=" +
        this.sectionId
    )
      .then((res) => res.text())
      .then((txt) => {
        let section = new DOMParser()
          .parseFromString(txt, "text/html")
          .querySelector(".shopify-section");
        this.innerHTML = section.innerHTML;
        // The page's title moves into the panel's sticky head (snippets/modal-usp.liquid), beside the close button.
        const title = this.querySelector(":scope > .h3");
        const slot = this.closest(".pdp-usp-modal-block")?.querySelector("[data-modal-title]");
        if (title && slot) {
          slot.textContent = title.textContent.trim();
          title.remove();
        }
        this.loaded = true;
      });
  }
}

if (!customElements.get("modalusp-content")) customElements.define("modalusp-content", ModalUSPContent);
